// Copyright (c) Hamilton Manufacturing Corp.
import React, { useContext, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { IconIndex } from '../_common/api/IconIndex';
import { QuestRoute } from '../_common/api/QuestRoutes';
import { between, generateId, shallowCompare, toggleArrayElement, updateNestedObjProp } from '../_common/api/utility';
import Draggable from '../_common/Draggable';
import Button from '../_common/form/Button';
import CheckBox from '../_common/form/CheckBox';
import Form from '../_common/form/Form';
import HamLink from '../_common/form/HamLink';
import Heading from '../_common/form/Heading';
import Input from '../_common/form/Input';
import Select from '../_common/form/Select';
import Title from '../_common/form/Title';
import { useGlobalState } from '../_common/globalState/GlobalStateProvider';
import Loading from '../_common/Loading';
import Modal from '../_common/Modal';
import MenuRoutes, { routes } from '../_pwa/MenuRoutes';
import Page from '../_ecom/pageLayout/Page';
import ColorPicker from './MAC/ColorPicker';
import Layout from '../_pwa/pageLayout/Layout';
import MenuRouteUpdater from './MAC/MenuRouteUpdater';
import ShopItemsUpdater from './MAC/ShopItemsUpdater';
import Icon from '../_common/form/Icon';
import Card from '../_pwa/Card';
import InputUpdater from './SiteConfig/InputUpdater';
import { Notif } from '../_pwa/Notification';
import { sanitizeSiteConfig } from '../../InitialState';
import { HamQuest } from '../../App';
import LogIn from '../_ecom/LogIn';
import { Regex } from '../_common/api/regex';

const IFrame = styled.iframe`
    width: 390px;
    height: 844px;
`
const Row = styled.div`
    display: flex;
`
const RowWrap = styled(Row)`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
`
const WarningButton = styled(Button)`
    background: ${({ theme }) => theme.error};
`
const Col = styled.div`
    display: flex;
    flex-direction: column;

    margin: 0.5em 0;
`
const Section = styled.div`
    display: flex;
    flex-direction: column;

    margin: 1em 0;
`

const EcomCard = styled(Card)`
    background: ${({ theme }) => theme.body};
    color: ${({ theme }) => theme.text};
`

const MAC = () => {
    const {
        state: {
            active_user,
            site_config,
            subdomain
        },
        setThemes,
        setHomeCards,
        getSiteConfig,
        updateSiteConfig
    } = useGlobalState();

    const { setNotification } = useContext(Notif);

    const configRef = useRef(null);
    const uploadImgRef = useRef(null);
    const uploadJsonRef = useRef(null);

    useLayoutEffect(() => {
        HamQuest.get(QuestRoute.merchant.siteConfig)
            .then(res => {
                sanitizeSiteConfig(res)
                setDefaultConfig(res)
            })

        HamQuest.get(QuestRoute.appVersion)
            .then(res => setCurrentAppVer(res))

        HamQuest.get(QuestRoute.firebaseProjectIds)
        .then(res => setFirebaseProjectIds(res)) 
    }, [])

    const [defaultConfig, setDefaultConfig] = useState(null);
    const [showMobile, setShowMobile] = useState(false);
    const [showConf, setShowConf] = useState(false);
    const [currentAppVer, setCurrentAppVer] = useState(null);
    const [firebaseProjectIds, setFirebaseProjectIds] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const enabledRoutes = MenuRoutes();

    if (!active_user.isAdmin) {
        return <LogIn nowrap />;
    }

    const updateTheme = (key, value) => {
        if (!key) return;
        const newTheme = { ...site_config.themes };
        updateNestedObjProp(newTheme, key, value)
        setThemes(newTheme);
    }

    const updateHomeCardColor = (key, value) => {
        if (!key) return;
        const newTheme = { ...site_config.themes };
        const path = key.split('.');
        path[1] = parseInt(path[1]);
        newTheme[path[0]].mobile.homeCards[path[1]][path[2]] = value;
        setThemes(newTheme);
    }

    const updateHomeCard = (newCard, index) => {
        let newCards = [...site_config.homeCards];

        if (index === false) {
            newCards.push({
                icon: 'home',
                text: 'New Card',
                to: '/',
            });
            const newThemeTemplate = {
                card: "#000000",
                icon: "#FFFFFF",
                text: "#FFFFFF",
            };
            const newTheme = { ...site_config.themes };
            newTheme.light.mobile.homeCards.push({ ...newThemeTemplate });
            // newTheme.dark.mobile.homeCards.push({ ...newThemeTemplate })
            setThemes(newTheme);
        } else if (!newCard) {
            newCards.splice(index, 1);
        } else {
            newCards[index] = newCard;
        }

        setHomeCards(newCards);
    }

    const pushSiteConfig = () => {
        Modal.Confirmation({
            title: 'Are you sure?',
            body: 'Are you sure you want to update the site config with this current configuration?',
            type: 'warning',
            hideClose: true,
            closeAfter: true,
        })
            .then(res => {
                if (!res) return;
                Modal.Promise({
                    title: 'Updating',
                    callback: HamQuest.post(QuestRoute.merchant.siteConfig, {
                        siteConfig: { ...site_config }
                    })
                })
                    .then(() =>
                        Modal.Show({
                            title: 'Success',
                            body: 'Configuration updated Successfully',
                            type: 'success',
                        })
                    )
                    .catch(ex =>
                        Modal.Show({
                            type: 'error',
                            title: 'Unable to Save new Configuration',
                            body: ex.blob || 'Something went wrong when updating the configuration. Please try again.'
                        })
                    )
            })
    }

    const updateSubdomain = () => {
        Modal.Show({
            title: 'Enter a new Subdomain',
            body: (
                <Form
                    inputs={{ subdomain: '' }}
                    onSubmit={e => (
                        Modal.Confirmation({
                            title: 'Are you sure?',
                            body: 'Are you sure you want to update the subdomain?',
                            type: 'warning',
                            hideClose: true,
                            closeAfter: true,
                        })
                            .then(res => {
                                if (!res) return;
                                Modal.Promise({
                                    title: 'Updating',
                                    callback: HamQuest.post(QuestRoute.merchant.subdomain, { ...e })
                                })
                                    .then(() => window.location = window.location.origin.replace(subdomain, e))
                                    .catch(ex =>
                                        Modal.Show({
                                            type: 'error',
                                            title: 'Unable to Save new Subdomain',
                                            body: ex.blob || 'Something went wrong when updating the configuration. Please try again.'
                                        })
                                    )
                            })
                    )}
                    localForm
                >
                    <Input
                        label='Subdomain'
                        name='subdomain'
                        type='text'
                        sanitize={Regex.match.specialChar}
                        required
                    />
                </Form>
            )
        })
    }

    const updateP2PCreds = () => {
        Modal.Promise({
            title: 'Fetching Creds',
            callback: HamQuest.get(QuestRoute.merchant.p2p)
        }).then(creds => (
            Modal.Show({
                title: 'Enter P2P Creds',
                body: (
                    <Form
                        inputs={{ terminalId: creds.terminalId || '' }}
                        onSubmit={e => (
                            Modal.Confirmation({
                                title: 'Are you sure?',
                                body: 'Are you sure you want to update the P2P Creds?',
                                type: 'warning',
                                hideClose: true,
                                closeAfter: true,
                            })
                                .then(res => {
                                    if (!res) return;
                                    Modal.Promise({
                                        title: 'Updating',
                                        callback: HamQuest.post(QuestRoute.merchant.p2p, { ...e })
                                    })
                                        .then(() => (
                                            Modal.Show({
                                                title: 'Success',
                                                body: 'Configuration updated Successfully',
                                                type: 'success',
                                            })
                                        ))
                                        .catch(ex =>
                                            Modal.Show({
                                                type: 'error',
                                                title: 'Unable to Save new Subdomain',
                                                body: ex.blob || 'Something went wrong when updating the configuration. Please try again.'
                                            })
                                        )
                                })
                        )}
                        localForm
                    >
                        <Input
                            label='Credentials'
                            name='terminalId'
                            type='text'
                            required
                        />
                    </Form>
                )
            })
        ))
    }

    const waringUpdate = () => {
        try {
            const newConfig = JSON.parse(configRef.current.innerHTML);

            console.log(newConfig)

            updateSiteConfig(newConfig)
        }
        catch (ex) {
            console.log(ex)
            Modal.Show({
                title: 'Config Error',
                body: 'Invalid Config.',
                type: 'error'
            })
        }
    }

    const MACToast = (toast) => {
        setNotification({
            ...toast,
            config: {
                position: 'top-right'
            }
        })
    }


    const downloadWrapper = async (OS) => {
        setDownloading(OS)
        const response = await fetch(window.manifestHREF);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const manifest = await response.json();
        HamQuest.get(QuestRoute.downloadWrapper(OS, manifest.name), {
            headers: {
                'Content-Type': 'application/zip'
            }
        })
            .then(response => response.blob())
            .then(blob => {
                const blobUrl = URL.createObjectURL(blob);

                const link = document.createElement("a");
                link.href = blobUrl;
                link.download = `${manifest.name} ${OS} Wrapper.zip`;

                document.body.appendChild(link);
                link.click();

                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
            })
            .catch(ex => {
                Modal.Show({
                    title: 'Error',
                    body: ex.blob,
                    type: 'error',
                })
            })
            .finally(() => setDownloading(false))
    }


    if (!defaultConfig) return <Loading />

    return (
        <Page>
            <Title icon='list'>MAC MAC 2.0</Title>

            <Button onClick={() => {
                Modal.Confirmation({
                    title: 'Are you sure?',
                    body: 'Are you sure you want to reset to the saved configuration?',
                    type: 'warning',
                    hideClose: true,
                    closeAfter: true,
                })
                    .then(res => {
                        if (!res) return;
                        getSiteConfig(true)
                    })
            }}
                disabled={shallowCompare(site_config, defaultConfig)}
            >
                Reset all values to saved values
            </Button>

            <Button
                onClick={pushSiteConfig}
                disabled={shallowCompare(site_config, defaultConfig)}
            >
                {shallowCompare(site_config, defaultConfig) ? 'Make Changes to the config' : 'Save and update Site config'}
            </Button>

            <Button
                onClick={() => downloadWrapper('IOS')}
                disabled={downloading}
                icon={downloading === 'IOS' ? 'spinner' : null}
            >
                Download IOS Wrapper
            </Button>
            <Button
                onClick={() => downloadWrapper('Android')}
                disabled={downloading}
                icon={downloading === 'Android' ? 'spinner' : null}
            >
                Download Android Wrapper
            </Button>

            <input
                ref={uploadImgRef}
                type='file'
                accept='.svg, .png, .mp4'
                multiple
                style={{ display: 'none' }}
                onInput={e => {
                    Modal.Confirmation({
                        title: 'Are you sure?',
                        body: 'Are you sure you want to upload a file?',
                        type: 'warning',
                        hideClose: true,
                        closeAfter: true,
                    })
                        .then(res => {
                            if (!res) return;
                            Modal.Promise({
                                title: 'Updating',
                                callback: HamQuest.postFile(QuestRoute.imgUpload, e.target.files, 'files')
                            })
                                .then(() =>
                                    Modal.Show({
                                        title: 'Success',
                                        body: 'File Uploaded',
                                        type: 'success',
                                    })
                                )
                                .catch(ex =>
                                    Modal.Show({
                                        type: 'error',
                                        title: 'Failed to upload',
                                        body: ex.blob || 'Something went wrong when updating the configuration. Please try again.'
                                    })
                                )
                        })
                }}
            />
            <Button onClick={() => uploadImgRef.current.click()}>Upload a File</Button>
 
            <Button onClick={() => updateSubdomain()}>Update Subdomain</Button>
            <Button onClick={updateP2PCreds}>Update P2P Credentials</Button>

            <Section>
                {showConf ?
                    <>
                        <Button onClick={() => setShowConf(false)}>Hide Raw Config</Button>
                        <WarningButton onClick={waringUpdate}>Update Config</WarningButton>
                        <pre
                            ref={configRef}
                            contentEditable='true'
                        >
                            {JSON.stringify(site_config, null, 4)}
                        </pre>
                    </>
                    : <Button onClick={() => Modal.Show({
                        title: 'Confirmation',
                        body: (
                            <Form
                                inputs={{ password: '' }}
                                onSubmit={e => {
                                    if (e.password === 'Hami%$26up') {
                                        setShowConf(true)
                                        Modal.Close();
                                    } else {
                                        Modal.Show({
                                            title: 'Incorrect Password',
                                            type: 'error'
                                        })
                                    }
                                }}
                                localForm
                            >
                                <Input
                                    label='Password'
                                    name='password'
                                    type='password'
                                />
                            </Form>
                        )
                    })
                    }>Show Raw Config</Button>}
            </Section>


            <input
                ref={uploadJsonRef}
                type='file'
                accept='.json'
                style={{ display: 'none' }}
                onChange={e => {
                    Modal.Confirmation({
                        title: 'Are you sure?',
                        body: 'Are you sure you want to upload a Firebase Private Key?',
                        type: 'warning',
                        hideClose: true,
                        closeAfter: true,
                    })
                        .then(res => {
                            if (!res) return;
                            Modal.Promise({
                                title: 'Updating',
                                callback: HamQuest.postFile(QuestRoute.firebaseUpload, e.target.files)
                            })
                                .then(() =>{
                                    HamQuest.get(QuestRoute.firebaseProjectIds)
                                        .then(res => setFirebaseProjectIds(res)) 
                                        
                                    Modal.Show({
                                        title: 'Success',
                                        body: 'Firebase Private Key uploaded Successfully',
                                        type: 'success',
                                    })
                                }
                                )
                                .catch(ex =>
                                    Modal.Show({
                                        type: 'error',
                                        title: 'Failed to upload',
                                        body: ex.blob || 'Something went wrong when updating the configuration. Please try again.'
                                    })
                                )
                        })
                }}
            />

            <EcomCard title='Firebase' collapsible>
                <Button onClick={() => uploadJsonRef.current.click()}>Upload Firebase Private Key</Button>           
    
               <Input
                    standalone
                    label='Private Key Project ID'
                    value={firebaseProjectIds?.privateKeyProjectId ?? ''}
                    readOnly
                />

                <Input
                    standalone
                    label='Config Project ID'
                    value={firebaseProjectIds?.configProjectId ?? ''}
                    readOnly
                />

                <InputUpdater
                    label='VAPID Key'
                    default={site_config.VAPIDKey || process.env.REACT_APP_VAPID_KEY}
                    onUpdate={(value, config) => config.VAPIDKey = value}
                />

                <Form
                    inputs={{ email: '' }}
                    onSubmit={e =>
                        HamQuest.post(QuestRoute.targetedNotification, { ...e })
                            .then(res => Modal.Show({
                                title: 'Notification Sent',
                                type: 'success',
                                closeAfter: true
                            }))
                    }
                    submitButton='Send Targeted Push Notification'
                >
                    <Input
                        label='Notification Email'
                        name='email'
                        type='email'
                        required
                    />
                </Form>
            </EcomCard>

            <EcomCard title='Ecom' collapsible>
                <Section>
                    <InputUpdater
                        checkbox
                        label='Has Ecom'
                        default={site_config.has_ecom}
                        onUpdate={(value, config) => config.has_ecom = value}
                    />

                    <InputUpdater
                        label='Company Name'
                        default={site_config.companyName}
                        onUpdate={(value, config) => config.companyName = value}
                    />
                    <InputUpdater
                        label='Company Phone'
                        default={site_config.contact.phone}
                        onUpdate={(value, config) => config.contact.phone = value}
                        type='number'
                    />
                    <InputUpdater
                        label='Company Logo'
                        default={site_config.logo}
                        onUpdate={(value, config) => config.logo = value}
                    />
                    <InputUpdater
                        label='Home Video'
                        default={site_config.homeVideo}
                        onUpdate={(value, config) => config.homeVideo = value}
                    />
                    <InputUpdater
                        label='Value Card Name'
                        default={site_config.cardName}
                        onUpdate={(value, config) => config.cardName = value}
                    />
                    <InputUpdater
                        label='Google Zoom'
                        default={site_config.googleMap.zoom}
                        onUpdate={(value, config) => config.googleMap.zoom = value}
                        type='number'
                    />
                    <InputUpdater
                        label='Google Center Lat'
                        default={site_config.googleMap.center.lat}
                        onUpdate={(value, config) => config.googleMap.center.lat = value}
                        type='number'
                    />
                    <InputUpdater
                        label='Google Center Lng'
                        default={site_config.googleMap.center.lng}
                        onUpdate={(value, config) => config.googleMap.center.lng = value}
                        type='number'
                    />

                    <EcomCard title='ECOM Routes' collapsible>
                        <Col>
                            <Heading>Menu Items</Heading>
                            {site_config.menuItems.map((e, i) => (
                                <Row key={`${i}-menuItems`}>
                                    <InputUpdater
                                        label={`${e.tag} Label`}
                                        default={site_config.menuItems[i].label}
                                        onUpdate={(value, config) => config.menuItems[i].label = value}
                                    />
                                    <InputUpdater
                                        label={`${e.tag} URL`}
                                        default={site_config.menuItems[i].href}
                                        onUpdate={(value, config) => {
                                            if (value.charAt(0) !== '/') {
                                                value = '/' + value
                                            }
                                            config.menuItems[i].href = value
                                        }}
                                    />
                                </Row>
                            ))}

                            <Heading>Shop Items</Heading>
                            {site_config.shopItems.map((e, i) => (
                                <Row key={`${i}-shopItems`}>
                                    <InputUpdater
                                        label={`${e.tag} Label`}
                                        default={site_config.shopItems[i].label}
                                        onUpdate={(value, config) => config.shopItems[i].label = value}
                                    />
                                    <InputUpdater
                                        label={`${e.tag} URL`}
                                        default={site_config.shopItems[i].href}
                                        onUpdate={(value, config) => {
                                            // if (value.charAt(0) !== '/') {
                                            //     value = '/' + value
                                            // }
                                            config.shopItems[i].href = value
                                        }}
                                    />
                                </Row>
                            ))}
                        </Col>
                    </EcomCard>

                    <EcomCard title='ECOM Carousel' collapsible>
                        {site_config.carousel.map((e, i) => (
                            <Col key={`${i}-carousel`}>
                                <Heading>Page {i + 1}</Heading>
                                <InputUpdater
                                    label='Headline'
                                    default={site_config.carousel[i].headline}
                                    onUpdate={(value, config) => config.carousel[i].headline = value}
                                />
                                <InputUpdater
                                    label='Text'
                                    default={site_config.carousel[i].text}
                                    onUpdate={(value, config) => config.carousel[i].text = value}
                                />
                                <Row>
                                    <InputUpdater
                                        select
                                        label='Tag'
                                        default={site_config.carousel[i].tag}
                                        onUpdate={(value, config) => {
                                            config.carousel[i].tag = value;
                                            config.carousel[i].href = value === '_SHOP' ? '_WASHES' : ''
                                        }}
                                        options={site_config.menuItems.map(e => {
                                            return {
                                                label: e.label,
                                                value: e.tag
                                            }
                                        })}
                                    />
                                    {site_config.carousel[i].tag === '_SHOP' &&
                                        <InputUpdater
                                            select
                                            label='Href'
                                            default={site_config.carousel[i].href}
                                            onUpdate={(value, config) => config.carousel[i].href = value}
                                            options={site_config.shopItems.map(e => {
                                                return {
                                                    label: e.label,
                                                    value: e.tag
                                                }
                                            })}
                                        />
                                    }
                                </Row>
                                <InputUpdater
                                    label='Button Text'
                                    default={site_config.carousel[i].buttonText}
                                    onUpdate={(value, config) => config.carousel[i].buttonText = value}
                                />
                                <InputUpdater
                                    label='Image'
                                    default={site_config.carousel[i].image}
                                    onUpdate={(value, config) => config.carousel[i].image = value}
                                />
                                <InputUpdater
                                    label='Background Color'
                                    default={site_config.carousel[i].color}
                                    onUpdate={(value, config) => config.carousel[i].color = value}
                                    type='color'
                                />
                                <Button onClick={() => {
                                    const config = { ...site_config };
                                    config.carousel.splice(i, 1);
                                    updateSiteConfig(config);
                                }}>Remove Page</Button>
                            </Col>
                        ))}
                        <Button onClick={() => {
                            const config = { ...site_config };
                            config.carousel.push({
                                headline: '',
                                text: '',
                                tag: '',
                                href: '',
                                buttonText: '',
                                image: '',
                            })
                            updateSiteConfig(config);
                        }}>Add Page</Button>
                    </EcomCard>

                    <EcomCard title='ECOM Themes' collapsible>
                        <Col>

                            <RowWrap>
                                <Col>
                                    <Heading>Light Theme</Heading>
                                    {
                                        Object
                                            .entries(site_config.themes.light)
                                            .sort((a, b) => a[0].localeCompare(b[0]))
                                            .map(([key, val]) =>
                                                <ColorPicker
                                                    key={generateId()}
                                                    objectKey={'light.' + key}
                                                    val={val}
                                                    originalVal={defaultConfig.themes.light[key]}
                                                    updateFunc={updateTheme}
                                                    light={true}
                                                    toast={{ body: 'Updated ECOM Light theme!' }}
                                                />
                                            )
                                    }
                                </Col>
                                {/*
                                <Col>
                                    <Heading>Dark Theme</Heading>
                                    {
                                        Object
                                            .entries(site_config.themes.dark)
                                            .sort((a, b) => a[0].localeCompare(b[0]))
                                            .map(([key, val]) => {
                                                return (
                                                    <ColorPicker
                                                        key={generateId()}
                                                        objectKey={'dark.' + key}
                                                        val={val}
                                                        originalVal={defaultConfig.themes.dark[key]}
                                                        updateFunc={updateTheme}
                                                        toast={{ body: 'Updated ECOM Dark theme!' }}
                                                    />
                                                )
                                            })
                                    }
                                </Col>
                                */}
                            </RowWrap>
                            <HamLink href='#' onClick={e => e.preventDefault()}>Test Link</HamLink>
                        </Col>
                    </EcomCard>
                </Section>
            </EcomCard>


            <EcomCard title='Modals' collapsible>
                <Section>
                    <Col>
                        <RowWrap>
                            <Col>
                                <Heading>Light Theme</Heading>
                                <Row>
                                    <Button onClick={() => {
                                        Modal.Show({
                                            title: 'TEST',
                                            body: 'This is a test',
                                            closeAfter: true
                                        })
                                    }}>Modal</Button>
                                    <Button onClick={() => {
                                        Modal.Show({
                                            title: 'TEST',
                                            type: 'success',
                                            body: 'This is a test',
                                            closeAfter: true
                                        })
                                    }}>Success</Button>

                                    <Button onClick={() => {
                                        Modal.Show({
                                            title: 'TEST',
                                            type: 'warning',
                                            body: 'This is a test',
                                            closeAfter: true
                                        })
                                    }}>Warning</Button>
                                    <Button onClick={() => {
                                        Modal.Show({
                                            title: 'TEST',
                                            type: 'error',
                                            body: 'This is a test',
                                            closeAfter: true
                                        })
                                    }}>Error</Button>
                                </Row>
                                {
                                    Object
                                        .entries(site_config.themes.light.modal)
                                        .map(([key, val]) =>
                                            <ColorPicker
                                                key={generateId()}
                                                objectKey={`light.modal.${key}`}
                                                val={val}
                                                originalVal={defaultConfig.themes.light.modal[key]}
                                                updateFunc={updateTheme}
                                                light={true}
                                                toast={{ body: 'Updated MODAL Light theme!' }}
                                            />
                                        )
                                }
                            </Col>
                        </RowWrap>
                    </Col>
                </Section>
            </EcomCard>

            <EcomCard title='Buttons' collapsible>
                <Section>
                    <Col>
                        <Button>Testing</Button>
                        <Button disabled>Testing</Button>
                        <RowWrap>
                            <Col>
                                <Heading>Light Theme</Heading>
                                {
                                    Object
                                        .entries(site_config.themes.light.button)
                                        .sort((a, b) => a[0].localeCompare(b[0]))
                                        .map(([key, val]) =>
                                            <ColorPicker
                                                key={generateId()}
                                                objectKey={`light.button.${key}`}
                                                val={val}
                                                originalVal={defaultConfig.themes.light.button[key]}
                                                updateFunc={updateTheme}
                                                light={true}
                                                toast={{ body: 'Updated BUTTON Light theme!' }}
                                            />
                                        )
                                }
                            </Col>
                            {/* 
                            <Col>
                                <Heading>Dark Theme</Heading>
                                {
                                    Object
                                        .entries(site_config.themes.dark.button)
                                        .sort((a, b) => a[0].localeCompare(b[0]))
                                        .map(([key, val]) => {
                                            return (
                                                <ColorPicker
                                                    key={generateId()}
                                                    objectKey={`dark.button.${key}`}
                                                    val={val}
                                                    originalVal={defaultConfig.themes.dark.button[key]}
                                                    updateFunc={updateTheme}
                                                    toast={{ body: 'Updated BUTTON Dark theme!' }}
                                                />
                                            )
                                        })
                                }
                            </Col> 
                            */}
                        </RowWrap>
                    </Col>
                </Section>
            </EcomCard>

            <EcomCard title='Input' collapsible>
                <Section>
                    <Col>
                        <Input
                            standalone
                            value='Input'
                            label='Input'
                            readOnly
                        />
                        <Input
                            standalone
                            value='Input'
                            label='Input'
                            disabled
                            readOnly
                        />
                        <CheckBox
                            standalone
                            label='Checkbox'
                            readOnly
                        />
                        <CheckBox
                            standalone
                            checked={true}
                            label='Checkbox'
                            disabled

                        />
                    </Col>
                    <RowWrap>
                        <Col>
                            <Heading>Light Theme</Heading>
                            {
                                Object
                                    .entries(site_config.themes.light.input)
                                    .sort((a, b) => a[0].localeCompare(b[0]))
                                    .map(([key, val], i) =>
                                        <ColorPicker
                                            key={generateId()}
                                            objectKey={`light.input.${key}`}
                                            val={val}
                                            originalVal={defaultConfig.themes.light.input[key]}
                                            updateFunc={updateTheme}
                                            light={true}
                                            toast={{ body: 'Updated INPUT Light theme!' }}
                                        />
                                    )
                            }
                        </Col>
                        {/* 
                        <Col>
                            <Heading>Dark Theme</Heading>
                            {
                                Object
                                    .entries(site_config.themes.dark.input)
                                    .sort((a, b) => a[0].localeCompare(b[0]))
                                    .map(([key, val]) => {
                                        return (
                                            <ColorPicker
                                                key={generateId()}
                                                objectKey={`dark.input.${key}`}
                                                val={val}
                                                originalVal={defaultConfig.themes.dark.input[key]}
                                                updateFunc={updateTheme}
                                                toast={{ body: 'Updated INPUT Dark theme!' }}
                                            />
                                        )
                                    })
                            }
                        </Col> 
                        */}
                    </RowWrap>
                </Section>
            </EcomCard>

            <EcomCard title='PWA' collapsible>
                <Section>
                    {
                        <Col>
                            <Row>
                                <Draggable>
                                    {showMobile ? <IFrame src={window.location.origin + '#MAC'} name='PWA' id='PWA' title='MobilePWA' /> : null}
                                    <Button onClick={() => setShowMobile(!showMobile)}>{showMobile ? 'Hide' : 'Show'} App</Button>
                                </Draggable>
                            </Row>

                            <InputUpdater
                                checkbox
                                label='Has PWA'
                                default={site_config.has_app}
                                onUpdate={(value, config) => config.has_app = value}
                            />
 
                            <InputUpdater
                                checkbox
                                label='Bubbles'
                                default={site_config.bubbles.enabled}
                                onUpdate={(value, config) => config.bubbles.enabled = value}
                            />
                            <InputUpdater
                                label='Number of Bubbles'
                                default={site_config.bubbles.number}
                                onUpdate={(value, config) => config.bubbles.number = between(value, 1, 20)}
                                type='number'
                            />
                            <InputUpdater
                                checkbox
                                label='Force Location Select'
                                default={site_config.forceLocations}
                                onUpdate={(value, config) => config.forceLocations = value}
                                type='number'
                            />
                            <InputUpdater
                                checkbox
                                label='Loyalty Program'
                                default={site_config.loyaltyProgram}
                                onUpdate={(value, config) => config.loyaltyProgram = value}
                            />
                            <InputUpdater
                                checkbox
                                label='Show PWA Body Shadow'
                                default={site_config.showPWAShadow}
                                onUpdate={(value, config) => config.showPWAShadow = value}
                            />

                            <InputUpdater
                                label='Apple App Store Link'
                                default={site_config.appStore.apple}
                                onUpdate={(value, config) => config.appStore.apple = value}
                            />
                            <InputUpdater
                                label='Google App Store Link'
                                default={site_config.appStore.google}
                                onUpdate={(value, config) => config.appStore.google = value}
                            />
                            <InputUpdater
                                label='PWA Background Image'
                                default={site_config.PWAHomeImage}
                                onUpdate={(value, config) => config.PWAHomeImage = value}
                            />

                            <EcomCard title='Home Cards' collapsible>

                                <RowWrap>
                                    {
                                        site_config
                                            .homeCards
                                            .map((e, i) =>
                                                <Col key={generateId()}>
                                                    <Heading>Home Card {i + 1}</Heading>
                                                    <Row>
                                                        <ColorPicker
                                                            label='Light Card'
                                                            objectKey={`light.${i}.card`}
                                                            val={site_config.themes.light.mobile.homeCards[i]?.card}
                                                            originalVal={defaultConfig.themes.light.mobile.homeCards[i]?.card}
                                                            updateFunc={updateHomeCardColor}
                                                            light={true}
                                                        />
                                                        <Button onClick={() => {
                                                            const config = { ...site_config }
                                                            const color = site_config.themes.light.mobile.homeCards[i].card;
                                                            config.themes.light.mobile.homeCards.forEach(f => f.card = color)
                                                            updateSiteConfig(config);
                                                        }}>Set All Cards</Button>
                                                    </Row>
                                                    <Row>
                                                        <ColorPicker
                                                            label='Light Text'
                                                            objectKey={`light.${i}.text`}
                                                            val={site_config.themes.light.mobile.homeCards[i]?.text}
                                                            originalVal={defaultConfig.themes.light.mobile.homeCards[i]?.text}
                                                            updateFunc={updateHomeCardColor}
                                                            light={true}
                                                        />
                                                        <Button onClick={() => {
                                                            const config = { ...site_config }
                                                            const color = site_config.themes.light.mobile.homeCards[i].text;
                                                            config.themes.light.mobile.homeCards.forEach(f => f.text = color)
                                                            updateSiteConfig(config);
                                                        }}>Set All Texts</Button>
                                                    </Row>
                                                    <Row>
                                                        <ColorPicker
                                                            label='Light Icon'
                                                            objectKey={`light.${i}.icon`}
                                                            val={site_config.themes.light.mobile.homeCards[i]?.icon}
                                                            originalVal={defaultConfig.themes.light.mobile.homeCards[i]?.icon}
                                                            updateFunc={updateHomeCardColor}
                                                            light={true}
                                                        />
                                                        <Button onClick={() => {
                                                            const config = { ...site_config }
                                                            const color = site_config.themes.light.mobile.homeCards[i].icon;
                                                            config.themes.light.mobile.homeCards.forEach(f => f.icon = color)
                                                            updateSiteConfig(config);
                                                        }}>Set All Icons</Button>
                                                    </Row>

                                                    {/*
                                                         <Row>
                                                            <ColorPicker
                                                                label='Dark Card'
                                                                objectKey={`dark.${i}.card`}
                                                                val={site_config.themes.dark.mobile.homeCards[i]?.card}
                                                                originalVal={defaultConfig.themes.dark.mobile.homeCards[i]?.card}
                                                                updateFunc={updateHomeCardColor}
                                                            />
                                                            <Button onClick={() => {
                                                                const config = { ...site_config }
                                                                const color = site_config.themes.dark.mobile.homeCards[i].card;
                                                                config.themes.dark.mobile.homeCards.forEach(f => f.card = color)
                                                                updateSiteConfig(config);
                                                            }}>Set All Cards</Button>
                                                        </Row>
                                                        <Row>
                                                            <ColorPicker
                                                                label='Dark Text'
                                                                objectKey={`dark.${i}.text`}
                                                                val={site_config.themes.dark.mobile.homeCards[i]?.text}
                                                                originalVal={defaultConfig.themes.dark.mobile.homeCards[i]?.text}
                                                                updateFunc={updateHomeCardColor}
                                                            />
                                                            <Button onClick={() => {
                                                                const config = { ...site_config }
                                                                const color = site_config.themes.dark.mobile.homeCards[i].text;
                                                                config.themes.dark.mobile.homeCards.forEach(f => f.text = color)
                                                                updateSiteConfig(config);
                                                            }}>Set All Texts</Button>
                                                        </Row>
                                                        <Row>
                                                            <ColorPicker
                                                                label='Dark Icon'
                                                                objectKey={`dark.${i}.icon`}
                                                                val={site_config.themes.dark.mobile.homeCards[i]?.icon}
                                                                originalVal={defaultConfig.themes.dark.mobile.homeCards[i]?.icon}
                                                                updateFunc={updateHomeCardColor}
                                                            />
                                                            <Button onClick={() => {
                                                                const config = { ...site_config }
                                                                const color = site_config.themes.dark.mobile.homeCards[i].icon;
                                                                config.themes.dark.mobile.homeCards.forEach(f => f.icon = color)
                                                                updateSiteConfig(config);
                                                            }}>Set All Icons</Button>
                                                        </Row> 
                                                        */}

                                                    <InputUpdater
                                                        label='Home Button Text'
                                                        default={e.text}
                                                        onUpdate={(value, config) => config.homeCards[i].text = value}
                                                    />
                                                    <InputUpdater
                                                        select
                                                        label={<>Icon <Icon icon={e.icon} /></>}
                                                        default={e.icon}
                                                        onUpdate={(value, config) => config.homeCards[i].icon = value}
                                                        options={
                                                            Object
                                                                .entries(IconIndex)
                                                                .map(([key, val]) => {
                                                                    return {
                                                                        value: key,
                                                                        label: key
                                                                    }
                                                                })
                                                                .sort((a, b) => a.label.localeCompare(b.label))
                                                        }
                                                    />
                                                    <InputUpdater
                                                        select
                                                        label='To Page'
                                                        default={e.to}
                                                        onUpdate={(value, config) => config.homeCards[i].to = value}
                                                        options={
                                                            Object
                                                                .entries(enabledRoutes)
                                                                .map(([key, val]) => {
                                                                    return {
                                                                        value: val.route,
                                                                        label: val.label
                                                                    }
                                                                })
                                                                .sort((a, b) => a.label.localeCompare(b.label))
                                                        }
                                                    />
                                                    <WarningButton
                                                        onClick={() => updateHomeCard(false, i)}
                                                    >Remove Card</WarningButton>
                                                </Col>
                                            )
                                    }
                                </RowWrap>

                                {site_config.homeCards.length < 8 &&
                                    <Button
                                        onClick={() => updateHomeCard(false, false)}
                                    >Add New Card</Button>
                                }
                            </EcomCard>

                            <EcomCard title='Loyalty Points' collapsible>
                                <Col>
                                    <Heading></Heading>
                                    <RowWrap>
                                        <Col>
                                            <Heading>Light Theme</Heading>
                                            {
                                                Object
                                                    .entries(site_config.themes.light.mobile.loyaltyCard)
                                                    .sort((a, b) => a[0].localeCompare(b[0]))
                                                    .map(([key, val]) =>
                                                        <ColorPicker
                                                            key={generateId()}
                                                            objectKey={`light.mobile.loyaltyCard.${key}`}
                                                            val={val}
                                                            originalVal={defaultConfig.themes.light.mobile.loyaltyCard[key]}
                                                            updateFunc={updateTheme}
                                                            light={true}
                                                            toast={{ body: 'Updated LOYALTY Light theme!' }}
                                                        />
                                                    )
                                            }
                                        </Col>
                                        {/* 
                                            <Col>
                                                <Heading>Dark Theme</Heading>
                                                {
                                                    Object
                                                        .entries(site_config.themes.dark.mobile.loyaltyCard)
                                                        .sort((a, b) => a[0].localeCompare(b[0]))
                                                        .map(([key, val]) => {
                                                            return (
                                                                <ColorPicker
                                                                    key={generateId()}
                                                                    objectKey={`dark.mobile.loyaltyCard.${key}`}
                                                                    val={val}
                                                                    originalVal={defaultConfig.themes.dark.mobile.loyaltyCard[key]}
                                                                    updateFunc={updateTheme}
                                                                    toast={{ body: 'Updated LOYALTY Dark theme!' }}
                                                                />
                                                            )
                                                        })
                                                }
                                            </Col> 
                                            */}
                                    </RowWrap>
                                </Col>
                            </EcomCard>

                            <EcomCard title='PWA Themes' collapsible>
                                <RowWrap>
                                    <Col>
                                        <Heading>Light Theme</Heading>
                                        {
                                            Object
                                                .entries(site_config.themes.light.mobile)
                                                .sort((a, b) => a[0].localeCompare(b[0]))
                                                .map(([key, val]) =>
                                                    <ColorPicker
                                                        key={generateId()}
                                                        objectKey={`light.mobile.${key}`}
                                                        val={val}
                                                        originalVal={defaultConfig.themes.light.mobile[key]}
                                                        updateFunc={updateTheme}
                                                        light={true}
                                                        toast={{ body: 'Updated PWA Light theme!' }}
                                                    />
                                                )
                                        }
                                    </Col>
                                    {/* 
                                        <Col>
                                            <Heading>Dark Theme</Heading>
                                            {
                                                Object
                                                    .entries(site_config.themes.dark.mobile)
                                                    .sort((a, b) => a[0].localeCompare(b[0]))
                                                    .map(([key, val]) => {
                                                        return (
                                                            <ColorPicker
                                                                key={generateId()}
                                                                objectKey={`dark.mobile.${key}`}
                                                                val={val}
                                                                originalVal={defaultConfig.themes.dark.mobile[key]}
                                                                updateFunc={updateTheme}
                                                                toast={{ body: 'Updated PWA Dark theme!' }}
                                                            />
                                                        )
                                                    })
                                            }
                                        </Col> 
                                        */}
                                </RowWrap>
                            </EcomCard>

                            <EcomCard title='Disabled PWA Routes' collapsible>
                                <span>Disabled Pages: {site_config.disabledRoutes.join(', ')}</span>
                                <Col>
                                    {
                                        Object
                                            .entries(enabledRoutes)
                                            .sort((a, b) => a[0].localeCompare(b[0]))
                                            .map(([key], i) =>
                                                <InputUpdater
                                                    key={`${i}-disRoute`}
                                                    checkbox
                                                    label={key}
                                                    default={site_config.disabledRoutes.includes(key)}
                                                    onUpdate={(_, config) => {
                                                        const newRoutes = [...site_config.disabledRoutes];
                                                        toggleArrayElement(newRoutes, key);
                                                        config.disabledRoutes = newRoutes;
                                                    }}
                                                />
                                            )
                                    }
                                </Col>
                            </EcomCard>
                        </Col>
                    }
                </Section>
            </EcomCard>
        </Page >
    );
}

export default MAC;